import React, { useState, useContext, useEffect, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import ActiveWalk from '../components/ActiveWalk';

const WalkerDashboard = () => {
  const { getToken } = useContext(AuthContext);
  const [availableNow, setAvailableNow] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [bookings, setBookings] = useState([]);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch('/api/bookings/pending', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  }, [getToken]);

  useEffect(() => {
    fetchBookings();
    getLocation();
  }, [fetchBookings]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (geoError) => {
        const messages = {
          1: 'Location permission was denied. Allow location access in your browser settings, then click "Update Location".',
          2: 'Your location is currently unavailable. Check your device\'s location/GPS settings.',
          3: 'Getting your location timed out. Please try again.'
        };
        setLocationError(messages[geoError.code] || `Could not get your location: ${geoError.message}`);
      },
      { timeout: 10000 }
    );
  };

  const toggleAvailable = async () => {
    try {
      const newState = !availableNow;
      await fetch('/api/walkers/available', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ available_now: newState })
      });

      if (newState && location) {
        // Update location when available
        await fetch('/api/walkers/location', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...location, available_now: newState })
        });
      }

      setAvailableNow(newState);
    } catch (err) {
      console.error('Error toggling availability:', err);
    }
  };

  const acceptBooking = async (bookingId) => {
    try {
      const response = await fetch(`/api/bookings/${bookingId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      // Collapses the "accepted" hand-off step straight into "confirmed" so the
      // walk immediately shows up as an active walk for both sides to track.
      if (data.walk_session) {
        await fetch(`/api/walks/${data.walk_session.id}/status`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'confirmed' })
        });
      }
      fetchBookings();
    } catch (err) {
      console.error('Error accepting booking:', err);
    }
  };

  return (
    <div className="dashboard">
      <ActiveWalk />
      <div className="walker-status">
        <h2>Walker Status</h2>
        <button
          className={`availability-btn ${availableNow ? 'available' : 'unavailable'}`}
          onClick={toggleAvailable}
        >
          {availableNow ? '✓ Available Now' : '✗ Not Available'}
        </button>
        {location && <p>Location: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</p>}
        {locationError && <p className="error">{locationError}</p>}
        <button onClick={getLocation}>📍 Update Location</button>
      </div>

      <div className="bookings-panel">
        <h2>Booking Requests ({bookings.length})</h2>
        {bookings.length === 0 ? (
          <p>No pending bookings.</p>
        ) : (
          <div className="bookings-list">
            {bookings.map((booking) => (
              <div key={booking.id} className="booking-card">
                <h3>{booking.dog_name} ({booking.breed})</h3>
                <p>Owner: {booking.first_name} {booking.last_name}</p>
                <p>Duration: {booking.scheduled_duration_minutes} minutes</p>
                <p>Price: £{booking.price_gbp}</p>
                <button onClick={() => acceptBooking(booking.id)}>Accept Booking</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalkerDashboard;
